import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getFeaturedVideos } from "@/utils/video";
import { formatRating, formatViews } from "@/utils/format";

export default function HeroCarousel() {
  const items = getFeaturedVideos();
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [items.length]);

  const go = (dir: number) => {
    setActive((prev) => (prev + dir + items.length) % items.length);
  };

  return (
    <section className="relative w-full h-[560px] md:h-[640px] lg:h-[720px] overflow-hidden bg-background-50">
      {items.map((video, i) => (
        <div
          key={video.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={video.backdrop || video.poster}
            alt={`${video.title}推荐封面`}
            title={`${video.title} - 在线播放高清视频`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/40" />
        </div>
      ))}

      <div className="relative z-10 h-full flex items-center px-4 md:px-8">
        <div className="w-full max-w-3xl animate-fade-up" key={active}>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/15 border border-accent-500/40 text-accent-400 text-xs md:text-sm font-medium mb-4">
            <i className="ri-fire-fill" /> 本周热播推荐
          </span>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-foreground-950 text-shadow-hero leading-tight">
            {items[active].title}
          </h1>
          <div className="flex flex-wrap items-center gap-3 md:gap-4 mt-4 text-sm md:text-base text-foreground-700">
            <span className="flex items-center gap-1 font-semibold text-accent-500">
              <i className="ri-star-fill" /> {formatRating(items[active].rating)}
            </span>
            <span>{items[active].typeLabel}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-500" />
            <span>{items[active].year}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-500" />
            <span>{items[active].region}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-500" />
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" /> {formatViews(items[active].views)}
            </span>
          </div>
          <p className="mt-4 max-w-xl text-sm md:text-base text-foreground-600 leading-relaxed line-clamp-3">
            {items[active].summary}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-7">
            <Link
              to={`/detail/${items[active].id}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md font-semibold whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-play-fill text-lg" /> 立即观看
            </Link>
            <Link
              to={`/detail/${items[active].id}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md font-semibold whitespace-nowrap bg-background-200/80 backdrop-blur-sm text-foreground-950 hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className="ri-information-line text-lg" /> 查看详情
            </Link>
          </div>
        </div>
      </div>

      {/* arrows */}
      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(-1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-100/70 backdrop-blur-sm text-foreground-950 hover:bg-background-200 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-100/70 backdrop-blur-sm text-foreground-950 hover:bg-background-200 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      {/* indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {items.map((video, i) => (
          <button
            key={video.id}
            type="button"
            aria-label={`切换到第${i + 1}张`}
            onClick={() => setActive(i)}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              i === active ? "w-8 bg-accent-500" : "w-3 bg-background-400/70 hover:bg-background-300"
            }`}
          />
        ))}
      </div>
    </section>
  );
}